import type { APIClientService } from '@/services/APIClientService'
import { PLAYER_LIVE_PREVIEW_MIN_UPLOAD_INTERVAL_MS } from '@lucimark/shared/helpers/playerLivePreview'
import { nextTick } from 'vue'
import { capturePlayerLivePreviewBlob } from '@/helpers/capturePlayerLivePreview'

const ROOT_MOUNT_RETRY_MS = 2_000
const CAPTURE_RETRY_MS = 30_000

let lastSuccessfulUploadAt = 0
let uploadInFlight = false
let uploadTimer: ReturnType<typeof setInterval> | null = null
let captureRetryTimer: ReturnType<typeof setTimeout> | null = null
let cadenceEnabled = false
let activeApiClient: APIClientService | null = null

function clearCaptureRetryTimer(): void {
  if (captureRetryTimer != null) {
    clearTimeout(captureRetryTimer)
    captureRetryTimer = null
  }
}

function scheduleCaptureRetry(apiClientService: APIClientService, delayMs: number): void {
  if (!cadenceEnabled || captureRetryTimer != null) {
    return
  }
  captureRetryTimer = setTimeout(() => {
    captureRetryTimer = null
    void maybeUploadPlayerLivePreview(apiClientService)
  }, delayMs)
}

async function waitForLivePreviewCaptureRoot(maxAttempts = 12): Promise<Element | null> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const root = document.querySelector('[data-player-live-preview-root]')
    if (root != null) {
      return root
    }
    await nextTick()
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
  }
  return document.querySelector('[data-player-live-preview-root]')
}

async function waitForCapturableLivePreviewBlob(maxAttempts = 40): Promise<Blob | null> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const blob = await capturePlayerLivePreviewBlob()
      if (blob != null && blob.size > 0) {
        return blob
      }
    }
    catch {
      // Tainted canvas or other capture failure — try next frame / candidate.
    }
    await nextTick()
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
  }
  return null
}

/** Called from `App.vue` when the playback root layer mounts (after Vue has painted). */
export function notifyLivePreviewPlaybackRootMounted(): void {
  if (!cadenceEnabled || activeApiClient == null) {
    return
  }
  void maybeUploadPlayerLivePreview(activeApiClient)
}

export async function maybeUploadPlayerLivePreview(apiClientService: APIClientService): Promise<void> {
  if (!cadenceEnabled) {
    return
  }
  const now = Date.now()
  if (uploadInFlight || now - lastSuccessfulUploadAt < PLAYER_LIVE_PREVIEW_MIN_UPLOAD_INTERVAL_MS) {
    return
  }
  const root = await waitForLivePreviewCaptureRoot()
  if (root == null) {
    scheduleCaptureRetry(apiClientService, ROOT_MOUNT_RETRY_MS)
    return
  }
  const blob = await waitForCapturableLivePreviewBlob()
  if (blob == null || blob.size <= 0) {
    scheduleCaptureRetry(apiClientService, ROOT_MOUNT_RETRY_MS)
    return
  }
  uploadInFlight = true
  try {
    await apiClientService.uploadLivePreview(blob)
    lastSuccessfulUploadAt = Date.now()
    clearCaptureRetryTimer()
  }
  catch {
    scheduleCaptureRetry(apiClientService, CAPTURE_RETRY_MS)
  }
  finally {
    uploadInFlight = false
  }
}

export function syncPlayerLivePreviewUploadCadence(options: {
  enabled: boolean
  apiClientService: APIClientService
}): void {
  cadenceEnabled = options.enabled
  activeApiClient = options.enabled ? options.apiClientService : null
  if (!options.enabled) {
    if (uploadTimer != null) {
      clearInterval(uploadTimer)
      uploadTimer = null
    }
    clearCaptureRetryTimer()
    return
  }
  if (uploadTimer != null) {
    return
  }
  void maybeUploadPlayerLivePreview(options.apiClientService)
  uploadTimer = setInterval(() => {
    void maybeUploadPlayerLivePreview(options.apiClientService)
  }, PLAYER_LIVE_PREVIEW_MIN_UPLOAD_INTERVAL_MS)
}
